import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { Hub } from 'aws-amplify/utils'
import {
  fetchAuthSession,
  getCurrentUser,
  signInWithRedirect,
  signOut as amplifySignOut,
} from 'aws-amplify/auth'

export interface AuthUser {
  userId: string
  username: string
  email?: string
  name?: string
}

interface AuthContextValue {
  user: AuthUser | null
  status: 'loading' | 'authenticated' | 'unauthenticated'
  signIn: () => Promise<void>
  signOut: () => Promise<void>
  getIdToken: () => Promise<string | null>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

const AUTH_DISABLED = import.meta.env.VITE_AUTH_DISABLED === 'true'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(
    AUTH_DISABLED
      ? { userId: 'local-dev', username: 'dev@local', email: 'dev@local', name: 'Local dev' }
      : null,
  )
  const [status, setStatus] = useState<AuthContextValue['status']>(
    AUTH_DISABLED ? 'authenticated' : 'loading',
  )

  const refreshUser = useCallback(async () => {
    if (AUTH_DISABLED) return
    try {
      const current = await getCurrentUser()
      const session = await fetchAuthSession()
      const claims = session.tokens?.idToken?.payload as Record<string, unknown> | undefined
      setUser({
        userId: current.userId,
        username: current.username,
        email: typeof claims?.email === 'string' ? claims.email : undefined,
        name:
          typeof claims?.name === 'string'
            ? claims.name
            : typeof claims?.['cognito:username'] === 'string'
              ? (claims['cognito:username'] as string)
              : undefined,
      })
      setStatus('authenticated')
    } catch {
      setUser(null)
      setStatus('unauthenticated')
    }
  }, [])

  useEffect(() => {
    if (AUTH_DISABLED) return
    void refreshUser()
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
        case 'signInWithRedirect':
        case 'tokenRefresh':
          void refreshUser()
          break
        case 'signedOut':
          setUser(null)
          setStatus('unauthenticated')
          break
      }
    })
    return () => {
      unsubscribe()
    }
  }, [refreshUser])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      signIn: async () => {
        if (AUTH_DISABLED) return
        await signInWithRedirect({ provider: 'Google' })
      },
      signOut: async () => {
        if (AUTH_DISABLED) return
        await amplifySignOut()
      },
      getIdToken: async () => {
        if (AUTH_DISABLED) return null
        const session = await fetchAuthSession()
        return session.tokens?.idToken?.toString() ?? null
      },
    }),
    [user, status],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
